import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getBscPractice, approveBscPractice, rejectBscPractice, discardBscPractice,
  reprocessBscPractice, uploadBscDocument, processBscDocument, fetchBscDocumentTags,
  deleteBscDocument, fetchBscActivity, markBscPracticeRead, bscOutputPreview,
} from "../../api/client";
import { StatusBadge } from "../../lib/status";
import { ClientSourceChip } from "../../lib/ClientSourceChip";
import { DetailPageShell } from "../../lib/DetailPageShell";
import { NoteModal } from "../../lib/NoteModal";
import { HanjaCertificateId, HanjaReadinessPanel } from "../../lib/hanja";
import type { BscPractice, BscData, BscStatus, BscRawEmail, BscDocType, BscManualDocument, AuditEntry } from "../../api/types";
import { BscPdfAccordion, getPdfsForDoc } from "../../components/BscPdfAccordion";
import { ConfirmModal } from "../../components/ConfirmModal";
import { toCountryIso2 } from "../../helpers/countryCode";

// ── Helpers ─────────────────────────────────────────────────────────────────

// Stato pratica reso via <StatusBadge> condiviso (lib/status)

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ── Definizioni campi ────────────────────────────────────────────────────────

type FieldDef = { key: keyof BscData; label: string; type?: "number"; multiline?: boolean };

// Incoterm che includono costo trasporto/assicurazione (C-group).
// Per questi il FOB e derivato: Total - Nolo - Assicurazione.
const C_INCOTERMS = ["CIF", "CIP", "CFR", "CPT"];

// Elenco Incoterm proposti nella select.
const INCOTERM_OPTIONS = [
  "EXW", "FCA", "FAS", "FOB",
  "CFR", "CIF", "CPT", "CIP",
  "DAP", "DPU", "DDP",
];

const BL_FIELDS: FieldDef[] = [
  { key: "bl_number", label: "Numero B/L" },
  { key: "vessel_name", label: "Nome nave" },
  { key: "voyage_number", label: "Numero viaggio" },
  { key: "pol", label: "Porto di carico (POL)" },
  { key: "pol_country", label: "Paese POL" },
  { key: "pod", label: "Porto di scarico (POD)" },
  { key: "pod_country", label: "Paese POD" },
  { key: "container_number", label: "Container" },
  { key: "seal_number", label: "Sigillo" },
  { key: "packages_number", label: "Colli" },
  { key: "gross_weight", label: "Peso lordo" },
  { key: "goods_description", label: "Descrizione merce" },
];

const INVOICE_FIELDS: FieldDef[] = [
  { key: "invoice_number", label: "Numero fattura" },
  { key: "invoice_date", label: "Data fattura" },
  { key: "invoice_total", label: "Totale fattura", type: "number" },
  { key: "invoice_currency", label: "Valuta" },
  { key: "importer_name", label: "Importatore (ragione sociale)" },
  { key: "importer_address", label: "Indirizzo importatore" },
  { key: "importer_city", label: "Città importatore" },
  { key: "importer_country", label: "Paese importatore" },
  { key: "importer_vat", label: "P.IVA importatore" },
  { key: "exporter_name", label: "Esportatore (ragione sociale)" },
  { key: "exporter_address", label: "Indirizzo esportatore" },
  { key: "exporter_city", label: "Città esportatore" },
  { key: "exporter_country", label: "Paese esportatore" },
  { key: "net_weight", label: "Peso netto" },
];

// Campi CIF: SEMPRE opzionali/supplementari, non contano mai come "mancanti"
// nel pannello sotto, indipendentemente dall'Incoterm — coerente col
// denominatore di completeness, che resta fisso a 26. Definiti qui solo per
// essere renderizzati come sezione editabile a parte ("Extra CIF").
const CIF_FIELDS: FieldDef[] = [
  { key: "terms_of_delivery", label: "Incoterm" },
  { key: "freight", label: "Nolo", type: "number" },
  { key: "insurance", label: "Assicurazione", type: "number" },
];

// Campi Packing List (Phase 5): supplementari, NON conteggiano in completeness.
const PACKING_LIST_FIELDS: FieldDef[] = [
  { key: "pl_number", label: "Riferimento" },
  { key: "pl_total_packages", label: "Colli totali" },
  { key: "pl_total_gross_weight", label: "Peso lordo totale" },
  { key: "pl_total_net_weight", label: "Peso netto totale" },
  { key: "pl_marks_numbers", label: "Marche e numeri", multiline: true },
];

// Campi EX1 / dichiarazione doganale (Phase 5): supplementari, NON conteggiano
// in completeness.
const EX1_FIELDS: FieldDef[] = [
  { key: "ex1_mrn", label: "MRN" },
  { key: "ex1_declaration_date", label: "Data dichiarazione" },
  { key: "ex1_customs_office", label: "Ufficio doganale" },
  { key: "ex1_reference", label: "Riferimento" },
];

// Cliente: chi scrive l'email a Frabemar, ricavato dalla firma nel corpo
// (src/bsc/client_extract.py). NON conteggia in completeness e NON entra nel
// JSON di output verso Frabemar — metadato dashboard, editabile dall'operatore.
const CLIENT_FIELDS: FieldDef[] = [
  { key: "client_name", label: "Nome cliente" },
  { key: "client_email", label: "Email cliente" },
];

// ── Accordion BL / Invoice (default chiuso, lazy load PDF iframe) ──────────

/** Bottone "Vedi testo OCR" / "Nascondi testo OCR" con pre scrollabile. */
function OcrToggle({ ocrText, hasPdf }: { ocrText: string; hasPdf: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ borderTop: hasPdf ? "1px solid var(--border-color)" : "none" }}>
      <div style={{ padding: "var(--space-2) var(--space-3)", background: "var(--indaco-gray-50)" }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setShow(!show)}
          style={{ fontSize: "var(--font-size-xs)", display: "flex", alignItems: "center", gap: 4 }}
        >
          <span className="material-symbols-outlined icon-sm">text_snippet</span>
          {show ? "Nascondi testo OCR" : "Vedi testo OCR"}
          <span className="text-muted" style={{ marginLeft: 4 }}>({ocrText.length.toLocaleString()} char)</span>
        </button>
      </div>
      {show && (
        <pre style={{
          margin: 0,
          padding: "var(--space-3)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "var(--font-family-mono)",
          fontSize: "var(--font-size-xs)",
          lineHeight: 1.5,
          color: "var(--indaco-gray-800)",
          maxHeight: 400,
          overflowY: "auto",
          borderTop: "1px solid var(--border-color)",
          background: "var(--indaco-gray-50)",
        }}>
          {ocrText}
        </pre>
      )}
    </div>
  );
}

/** Wrapper locale che usa il componente condiviso BscPdfAccordion + OcrToggle.
 *  Mantiene la stessa API del vecchio DocAccordion per non toccare DocumentsPanel. */
function DocAccordion({
  practiceId,
  title,
  icon,
  headerColor,
  headerBg,
  hasDoc,
  ocrText,
  pdfFilenames,
  headerActions,
}: {
  practiceId: string;
  title: string;
  icon: string;
  headerColor: string;
  headerBg: string;
  hasDoc: boolean;
  ocrText: string;
  pdfFilenames: string[];
  headerActions?: React.ReactNode;
}) {
  return (
    <BscPdfAccordion
      practiceId={practiceId}
      title={title}
      icon={icon}
      headerColor={headerColor}
      headerBg={headerBg}
      pdfFilenames={pdfFilenames}
      headerActions={headerActions}
    >
      {ocrText && <OcrToggle ocrText={ocrText} hasPdf={pdfFilenames.length > 0} />}
      {!pdfFilenames.length && !ocrText && (
        <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--indaco-gray-400)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40 }}>picture_as_pdf</span>
          <div className="text-sm">PDF non disponibile per questa pratica</div>
        </div>
      )}
      {!pdfFilenames.length && ocrText && (
        <div style={{
          display: "flex", alignItems: "center", gap: "var(--space-2)",
          padding: "var(--space-3)", background: "var(--indaco-warning-light)",
          borderBottom: "1px solid var(--border-color)",
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-warning)" }}>info</span>
          <span className="text-xs text-muted">PDF non disponibile — il testo estratto è accessibile dal bottone qui sotto</span>
        </div>
      )}
      {/* hasDoc false → badge "mancante" già gestito dal componente quando pdfFilenames è vuoto */}
      {!hasDoc && pdfFilenames.length === 0 && null}
    </BscPdfAccordion>
  );
}

// Divide l'ocr_text combinato in BL / Invoice
function splitOcrText(ocrText: string | null | undefined): { blText: string; invoiceText: string; plText: string; ex1Text: string } {
  if (!ocrText) return { blText: "", invoiceText: "", plText: "", ex1Text: "" };
  // Estrae ciascun blocco fino al prossimo marker. I marker PL/EX1 (Phase 5)
  // possono essere assenti (pratiche pre-Phase 5 o senza quel documento).
  const block = (marker: string): string => {
    const re = new RegExp(`={2,}\\s*${marker}\\s*={2,}([\\s\\S]*?)(?=={2,}\\s*(?:BILL OF LADING|INVOICE|PACKING LIST|EX1)\\s*={2,}|$)`, "i");
    const m = ocrText.match(re);
    return m ? m[1].trim() : "";
  };
  const blText = block("BILL OF LADING");
  const invoiceText = block("INVOICE");
  const plText = block("PACKING LIST");
  const ex1Text = block("EX1");
  return {
    blText: blText || (ocrText && !invoiceText && !plText && !ex1Text ? ocrText.trim() : ""),
    invoiceText,
    plText,
    ex1Text,
  };
}

/** Upload CONTESTUALE del documento MANCANTE, nel riquadro BL/Fattura — non
 *  nel tasto generico in fondo (l'operatore deve sempre agire dal box del
 *  documento giusto, non da un uploader ambiguo). Usa la STESSA logica
 *  dell'upload manuale (uploadBscDocument -> processBscDocument): analisi +
 *  riconoscimento tipo + estrazione campi + aggiunta al JSON.
 *
 *  Per la SOSTITUZIONE di un documento già presente vedi ``ReplaceDocIconButton``
 *  (icona compatta nell'header dell'accordion, accanto al download — non un
 *  box separato, su richiesta 2026-07-27). */
function ContextualMissingDocUpload({
  practiceId, slot, onDone, showToast,
}: {
  practiceId: string;
  slot: "bl" | "invoice";
  onDone: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const label = slot === "bl" ? "Bill of Lading" : "Fattura";

  async function onFile(file: File) {
    if (!file.name.toLowerCase().endsWith(".pdf")) { showToast("Solo file PDF sono supportati", false); return; }
    setBusy(true);
    try {
      const up = await uploadBscDocument(practiceId, file);
      const proc = await processBscDocument(practiceId, up.filename);
      const filledSlot = up.doc_type === slot || up.fills_missing_slot;
      const n = proc.updated_fields?.length ?? 0;
      showToast(
        filledSlot
          ? `${label} aggiunto — ${n} camp${n === 1 ? "o" : "i"} compilat${n === 1 ? "o" : "i"}`
          : `Documento caricato come ${DOC_TYPE_LABELS[up.doc_type]} (atteso ${label}): verifica`,
        filledSlot,
      );
      onDone();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore upload documento", false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{
      borderLeft: "4px solid var(--indaco-warning)", padding: "var(--space-3)",
      display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap",
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-warning)" }}>upload_file</span>
      <span className="text-sm"><strong>{label}</strong> mancante — caricalo qui</span>
      <input ref={inputRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => inputRef.current?.click()}
        style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4 }}>
        {busy ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <span className="material-symbols-outlined icon-sm">add</span>}
        Carica {label}
      </button>
    </div>
  );
}

/** Icona compatta per SOSTITUIRE un documento già presente — accanto al
 * download nell'header dell'accordion (stessa forma, freccia opposta:
 * "upload" invece di "download"), niente box separato (2026-07-27).
 * Stessa API BE di ContextualMissingDocUpload (upload -> process, merge
 * "il nuovo prevale" lato server) ma senza affermazione visiva dedicata. */
function ReplaceDocIconButton({
  practiceId, slot, isApproved, onDone, showToast,
}: {
  practiceId: string;
  slot: "bl" | "invoice";
  isApproved: boolean;
  onDone: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const label = slot === "bl" ? "Bill of Lading" : "Fattura";

  async function onFile(file: File) {
    if (!file.name.toLowerCase().endsWith(".pdf")) { showToast("Solo file PDF sono supportati", false); return; }
    setBusy(true);
    try {
      const up = await uploadBscDocument(practiceId, file);
      const proc = await processBscDocument(practiceId, up.filename);
      const filledSlot = up.doc_type === slot || up.fills_missing_slot;
      const n = proc.updated_fields?.length ?? 0;
      const reopened = isApproved ? " — pratica riaperta" : "";
      showToast(
        filledSlot
          ? `${label} sostituita${reopened}, ${n} camp${n === 1 ? "o" : "i"} aggiornat${n === 1 ? "o" : "i"}`
          : `Documento caricato come ${DOC_TYPE_LABELS[up.doc_type]} (atteso ${label}): verifica`,
        filledSlot,
      );
      onDone();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore upload documento", false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      <button
        className="btn btn-outline"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        style={{ padding: "2px 10px" }}
        title={`Sostituisci ${label}${isApproved ? " (riapre la pratica)" : ""}`}
      >
        {busy
          ? <span className="spinner" style={{ width: 14, height: 14 }} />
          : <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: "middle" }}>upload</span>}
      </button>
    </>
  );
}

function DocumentsPanel({
  practice, onProcessed, showToast, isReadOnly,
}: {
  practice: BscPractice;
  onProcessed: () => void;
  showToast: (msg: string, ok?: boolean) => void;
  isReadOnly: boolean;
}) {
  // La SOSTITUZIONE di un documento è ammessa anche su pratica APPROVATA (la
  // riapre), esattamente come il trigger via email: è la specifica del cliente
  // per entrambi i canali. Il gate è quindi `status !== "discarded"`, allineato
  // al backend (_check_practice_editable con allow_approved_reopen=True, dove
  // solo 'discarded' è bloccato). NON usare `isReadOnly`: quello governa la
  // modifica dei CAMPI, e usarlo qui rendeva morto il ramo isApproved di
  // ReplaceDocIconButton — bug segnalato il 2026-07-29.
  const canUploadDocs = practice.status !== "discarded";
  const { blText, invoiceText, plText, ex1Text } = splitOcrText(practice.ocr_text);
  const blPdfs = getPdfsForDoc(practice.pdf_files, "bl");
  const invPdfs = getPdfsForDoc(practice.pdf_files, "invoice");
  const plPdfs = getPdfsForDoc(practice.pdf_files, "packing_list");
  const ex1Pdfs = getPdfsForDoc(practice.pdf_files, "ex1");

  return (
    <>
      <DocAccordion
        practiceId={practice.id}
        title="Bill of Lading"
        icon="directions_boat"
        headerColor="var(--indaco-brand-primary)"
        headerBg="var(--indaco-brand-primary-light)"
        hasDoc={practice.has_bl}
        ocrText={blText}
        pdfFilenames={blPdfs}
        headerActions={
          practice.has_bl && canUploadDocs ? (
            <ReplaceDocIconButton
              practiceId={practice.id} slot="bl" isApproved={practice.status === "approved"}
              onDone={onProcessed} showToast={showToast}
            />
          ) : undefined
        }
      />
      {!practice.has_bl && canUploadDocs && (
        <ContextualMissingDocUpload practiceId={practice.id} slot="bl" onDone={onProcessed} showToast={showToast} />
      )}
      <DocAccordion
        practiceId={practice.id}
        title="Fattura commerciale"
        icon="receipt_long"
        headerColor="var(--indaco-success)"
        headerBg="var(--indaco-success-light)"
        hasDoc={practice.has_invoice}
        ocrText={invoiceText}
        pdfFilenames={invPdfs}
        headerActions={
          practice.has_invoice && canUploadDocs ? (
            <ReplaceDocIconButton
              practiceId={practice.id} slot="invoice" isApproved={practice.status === "approved"}
              onDone={onProcessed} showToast={showToast}
            />
          ) : undefined
        }
      />
      {!practice.has_invoice && canUploadDocs && (
        <ContextualMissingDocUpload practiceId={practice.id} slot="invoice" onDone={onProcessed} showToast={showToast} />
      )}
      {(plPdfs.length > 0 || plText) && (
        <DocAccordion
          practiceId={practice.id}
          title="Packing List"
          icon="inventory_2"
          headerColor="var(--indaco-info)"
          headerBg="var(--indaco-info-light)"
          hasDoc={plPdfs.length > 0 || !!plText}
          ocrText={plText}
          pdfFilenames={plPdfs}
        />
      )}
      {(ex1Pdfs.length > 0 || ex1Text) && (
        <DocAccordion
          practiceId={practice.id}
          title="Dichiarazione doganale EX1"
          icon="verified_user"
          headerColor="var(--indaco-warning)"
          headerBg="var(--indaco-warning-light)"
          hasDoc={ex1Pdfs.length > 0 || !!ex1Text}
          ocrText={ex1Text}
          pdfFilenames={ex1Pdfs}
        />
      )}
    </>
  );
}

// ── Email originali (HTML renderizzato + fallback testo) ────────────────────

/** Strip HTML: rimuove tag, decodifica entita' base, collassa whitespace. */
function htmlToText(html: string): string {
  if (!html) return "";
  return html
    // Rimuovi <head> e <style> interamente
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    // Blocchi → newline
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    // Rimuovi tutti i tag
    .replace(/<[^>]+>/g, "")
    // Decodifica entità HTML comuni
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&agrave;/g, "à").replace(/&egrave;/g, "è").replace(/&igrave;/g, "ì")
    .replace(/&ograve;/g, "ò").replace(/&ugrave;/g, "ù")
    .replace(/&eacute;/g, "é")
    // Collassa whitespace
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** True se il body e' HTML (contiene tag rilevanti). */
function isHtmlBody(body: string): boolean {
  return /<[a-z][\s\S]*>/i.test(body);
}

// ── Upload manuale documenti (drag&drop / file picker) ──────────────────────

const DOC_TYPE_LABELS: Record<BscDocType, string> = {
  bl: "Bill of Lading",
  invoice: "Fattura commerciale",
  packing_list: "Packing List",
  ex1: "Dichiarazione doganale EX1",
  other: "Altro",
};

/** Pannello upload documenti BSC — drag&drop o file picker.
 *
 * Due casi gestiti dallo stesso componente (stesso endpoint BE):
 * 1. Slot mancante (BL o Invoice): upload -> se il documento viene
 *    classificato come quel tipo, propone di completare lo slot.
 * 2. Documento di supporto extra: se riconosciuto (Packing List/EX1) va
 *    dritto; se non riconosciuto ('other') chiede un tag libero prima
 *    di poter processare.
 *
 * In ENTRAMBI i casi, dopo l'upload la UI chiede sempre conferma esplicita
 * prima di elaborare il documento (mai auto-process silenzioso).
 */
function DocumentUploadPanel({
  practice,
  onProcessed,
  showToast,
}: {
  practice: BscPractice;
  onProcessed: () => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  // Documento appena caricato, in attesa che l'operatore confermi il processing.
  const [uploaded, setUploaded] = useState<{ filename: string; doc_type: BscDocType; fills_missing_slot: boolean } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Documento selezionato per la cancellazione, in attesa di conferma esplicita.
  const [deleteTarget, setDeleteTarget] = useState<BscManualDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchBscDocumentTags().then(r => setTagSuggestions(r.tags)).catch(() => { /* autocomplete best-effort */ });
  }, []);

  const missingSlotLabel = !practice.has_bl ? "Bill of Lading" : !practice.has_invoice ? "Fattura commerciale" : null;

  async function doUpload(file: File, tag: string) {
    setIsUploading(true);
    try {
      const res = await uploadBscDocument(practice.id, file, tag || undefined);
      setUploaded({ filename: res.filename, doc_type: res.doc_type, fills_missing_slot: res.fills_missing_slot });
      setPendingFile(null);
      setTagInput("");
      showToast(`Documento "${res.filename}" caricato (${DOC_TYPE_LABELS[res.doc_type]})`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore upload documento", false);
      throw e;
    } finally {
      setIsUploading(false);
    }
  }

  async function confirmUploadWithTag() {
    if (!pendingFile) return;
    try {
      await doUpload(pendingFile, tagInput);
    } catch {
      // gestito in doUpload
    }
  }

  async function handleUploadDirect(file: File) {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      showToast("Solo file PDF sono supportati", false);
      return;
    }
    setPendingFile(null);
    try {
      await doUpload(file, "");
    } catch (e) {
      // Se il BE richiede un tag (422), doUpload ha già mostrato il toast
      // generico; ripresentiamo il file per far inserire il tag.
      setPendingFile(file);
      void e;
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleUploadDirect(f);
  }

  async function handleProcessConfirm() {
    if (!uploaded) return;
    setIsProcessing(true);
    try {
      const res = await processBscDocument(practice.id, uploaded.filename);
      const extra = res.additional_fields.length > 0 ? `, ${res.additional_fields.length} campi aggiuntivi salvati` : "";
      showToast(`Documento elaborato: ${res.updated_fields.length} campi aggiornati${extra} (completezza ${Math.round(res.completeness * 100)}%)`);
      setUploaded(null);
      onProcessed();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore elaborazione documento", false);
    } finally {
      setIsProcessing(false);
    }
  }

  function handleProcessSkip() {
    setUploaded(null);
    onProcessed(); // ricarica comunque: il documento e' salvato come archiviato non processato
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const res = await deleteBscDocument(practice.id, deleteTarget.filename);
      const revertMsg = res.reverted_fields.length > 0
        ? `, ${res.reverted_fields.length} campi riportati a vuoto`
        : "";
      showToast(`Documento "${deleteTarget.filename}" cancellato${revertMsg}`);
      setDeleteTarget(null);
      onProcessed(); // ricarica la pratica: bsc_data/slot/status potrebbero essere cambiati
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore cancellazione documento", false);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="card" style={{ padding: "var(--space-3) var(--space-4)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-brand-primary)" }}>upload_file</span>
        <span className="text-sm fw-semibold">Carica documento</span>
      </div>

      {missingSlotLabel && (
        <div className="text-xs" style={{ color: "var(--indaco-warning)", marginBottom: "var(--space-2)" }}>
          Manca: {missingSlotLabel}. Se carichi il documento corrispondente, la pratica potrà completarsi.
        </div>
      )}

      {/* Dropzone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => e.key === "Enter" && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); }}
        style={{
          cursor: isUploading ? "wait" : "pointer",
          border: `2px dashed ${isDragging ? "var(--indaco-brand-primary)" : "var(--indaco-gray-300)"}`,
          borderRadius: "var(--border-radius-lg)",
          background: isDragging ? "var(--indaco-brand-primary-light)" : "var(--indaco-gray-50)",
          padding: "var(--space-4)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-1)",
          textAlign: "center",
          userSelect: "none",
        }}
      >
        {isUploading ? (
          <div className="spinner" />
        ) : (
          <>
            <span className="material-symbols-outlined icon-xl" style={{ color: isDragging ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)" }}>
              picture_as_pdf
            </span>
            <div className="text-sm" style={{ color: isDragging ? "var(--indaco-brand-primary)" : "var(--indaco-gray-700)" }}>
              {isDragging ? "Rilascia qui" : "Trascina un PDF qui o clicca per selezionarlo"}
            </div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          style={{ display: "none" }}
          onChange={e => {
            const f = e.target.files?.[0] ?? null;
            if (f) void handleUploadDirect(f);
            if (inputRef.current) inputRef.current.value = "";
          }}
        />
      </div>

      {/* Documento non riconosciuto: richiede un tag libero prima di riprovare l'upload. */}
      {pendingFile && (
        <div style={{ marginTop: "var(--space-2)", padding: "var(--space-3)", border: "1px solid var(--indaco-warning)", borderRadius: "var(--border-radius-md)", background: "var(--indaco-warning-light)" }}>
          <div className="text-sm" style={{ marginBottom: "var(--space-2)" }}>
            <span className="fw-semibold">{pendingFile.name}</span> non è stato riconosciuto automaticamente
            (non è BL/Invoice/Packing List/EX1). Specifica un tag libero per identificarlo prima di caricarlo.
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <input
              className="form-input"
              list="bsc-document-tags"
              placeholder="Es. Certificato di origine"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              style={{ flex: 1 }}
            />
            <datalist id="bsc-document-tags">
              {tagSuggestions.map(t => <option key={t} value={t} />)}
            </datalist>
            <button className="btn btn-primary btn-sm" onClick={confirmUploadWithTag} disabled={!tagInput.trim() || isUploading}>
              Carica
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setPendingFile(null); setTagInput(""); }} disabled={isUploading}>
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Conferma elaborazione post-upload — mai automatica. */}
      {uploaded && (
        <div style={{ marginTop: "var(--space-2)", padding: "var(--space-3)", border: "1px solid var(--indaco-brand-primary)", borderRadius: "var(--border-radius-md)", background: "var(--indaco-brand-primary-light)" }}>
          <div className="text-sm" style={{ marginBottom: "var(--space-2)" }}>
            <span className="fw-semibold">{uploaded.filename}</span> caricato come <span className="badge badge-info text-xs">{DOC_TYPE_LABELS[uploaded.doc_type]}</span>.
            {uploaded.fills_missing_slot && " Può completare il documento mancante."}
            {" "}Vuoi elaborarlo ora?
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <button className="btn btn-primary btn-sm" onClick={handleProcessConfirm} disabled={isProcessing}>
              {isProcessing ? "Elaborazione…" : "Sì, elabora ora"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={handleProcessSkip} disabled={isProcessing}>
              No, lascialo solo archiviato
            </button>
          </div>
        </div>
      )}

      {/* Elenco documenti caricati manualmente finora */}
      {(practice.manual_documents ?? []).length > 0 && (
        <div style={{ marginTop: "var(--space-2)" }}>
          <div className="text-xs text-muted" style={{ marginBottom: "var(--space-1)" }}>Documenti caricati manualmente</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
            {(practice.manual_documents as BscManualDocument[]).map(doc => (
              <div key={doc.filename} className="text-xs" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <span className="material-symbols-outlined icon-sm">description</span>
                <span className="truncate" style={{ flex: 1 }}>{doc.filename}</span>
                <span className="badge badge-neutral badge-sm">{doc.tag || DOC_TYPE_LABELS[doc.doc_type]}</span>
                <span className={`badge ${doc.processed ? "badge-success" : "badge-warning"} badge-sm`}>
                  {doc.processed ? "elaborato" : "in attesa"}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  title="Cancella documento"
                  onClick={() => setDeleteTarget(doc)}
                  style={{ padding: "2px 4px", lineHeight: 0 }}
                >
                  <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-danger)" }}>delete</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Cancellare questo documento?"
          danger
          confirmLabel={isDeleting ? "Cancellazione…" : "Sì, cancella"}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={isDeleting}
          message={
            <div>
              <p style={{ margin: "0 0 var(--space-2)" }}>
                <strong>{deleteTarget.filename}</strong> verrà rimosso dalla pratica (file e dati collegati).
              </p>
              {deleteTarget.processed && (deleteTarget.updated_fields?.length ?? 0) > 0 && (
                <p style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-700)" }}>
                  I campi che questo documento aveva compilato torneranno vuoti
                  (a meno che non siano stati scritti anche da un altro documento).
                </p>
              )}
              {deleteTarget.processed && deleteTarget.fills_missing_slot && (
                <p style={{ margin: 0, fontSize: "var(--font-size-sm)", color: "var(--indaco-warning)" }}>
                  Questo documento completava lo slot {DOC_TYPE_LABELS[deleteTarget.doc_type]}: tornerà a risultare mancante.
                </p>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}

function EmailsPanel({ emails }: { emails: BscRawEmail[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const [viewMode, setViewMode] = useState<"html" | "text">("html");
  if (!emails || emails.length === 0) return null;

  return (
    <div className="card" style={{ padding: "var(--space-3)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-2)" }}>
        <h3 style={{ margin: 0, fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>mail</span>
          Email originale
          {emails.length > 1 && <span className="badge badge-info">{emails.length}</span>}
        </h3>
        <div style={{ display: "flex", gap: 2, padding: 2, background: "var(--indaco-gray-100)", borderRadius: "var(--border-radius-sm)" }}>
          <button className={`btn btn-sm ${viewMode === "html" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setViewMode("html")} style={{ padding: "2px 8px", fontSize: "var(--font-size-xs)" }}>
            HTML
          </button>
          <button className={`btn btn-sm ${viewMode === "text" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setViewMode("text")} style={{ padding: "2px 8px", fontSize: "var(--font-size-xs)" }}>
            Testo
          </button>
        </div>
      </div>
      {emails.map((email, idx) => {
        const isOpen = openIdx === idx;
        const isHtml = isHtmlBody(email.body || "");
        return (
          <div key={idx} style={{ marginBottom: idx < emails.length - 1 ? "var(--space-2)" : 0, borderBottom: idx < emails.length - 1 ? "1px solid var(--indaco-gray-100)" : "none", paddingBottom: "var(--space-2)" }}>
            <div
              role="button"
              tabIndex={0}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "var(--space-1)" }}
              onClick={() => setOpenIdx(isOpen ? null : idx)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenIdx(isOpen ? null : idx); } }}
            >
              <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-gray-500)" }}>
                {isOpen ? "expand_less" : "expand_more"}
              </span>
              <span className="fw-semibold text-sm truncate" style={{ flex: 1 }}>{email.subject || "(senza oggetto)"}</span>
            </div>
            {isOpen && (
              <div style={{ marginTop: "var(--space-2)", paddingLeft: "var(--space-3)" }}>
                <div style={{ marginBottom: "var(--space-1)" }}>
                  <div className="text-muted text-xs">Mittente</div>
                  <div className="text-sm">{email.sender || "—"}</div>
                </div>
                <div style={{ marginBottom: "var(--space-1)" }}>
                  <div className="text-muted text-xs">Ricevuta</div>
                  <div className="text-sm">{fmt(email.received_at)}</div>
                </div>
                {email.body && (
                  <div style={{ marginTop: "var(--space-2)" }}>
                    <div className="text-muted text-xs" style={{ marginBottom: 2 }}>Corpo</div>
                    {viewMode === "html" && isHtml ? (
                      <iframe
                        srcDoc={email.body}
                        sandbox=""
                        title={`Email ${idx + 1}`}
                        style={{
                          width: "100%", minHeight: 200, maxHeight: 400,
                          border: "1px solid var(--border-color)",
                          borderRadius: "var(--border-radius-sm)",
                          background: "var(--indaco-white)",
                        }}
                      />
                    ) : (
                      <div style={{
                        marginTop: "var(--space-1)", maxHeight: 300, overflowY: "auto",
                        padding: "var(--space-2)", background: "var(--indaco-gray-50)",
                        borderRadius: "var(--border-radius-sm)", border: "1px solid var(--border-color)",
                        whiteSpace: "pre-wrap", fontSize: "var(--font-size-xs)", lineHeight: 1.5,
                      }}>
                        {isHtml ? htmlToText(email.body) : email.body}
                      </div>
                    )}
                  </div>
                )}
                {email.attachments_text && email.attachments_text.length > 0 && (
                  <div style={{ marginTop: "var(--space-2)" }}>
                    <div className="text-muted text-xs">Allegati ({email.attachments_text.length})</div>
                    <ul style={{ margin: "var(--space-1) 0 0", paddingLeft: "var(--space-4)", fontSize: "var(--font-size-xs)" }}>
                      {email.attachments_text.map((a, i) => (
                        <li key={i} title={a.filename} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <span className="material-symbols-outlined icon-sm" style={{ verticalAlign: "middle", marginRight: 4 }}>attach_file</span>
                          {a.filename} <span className="text-muted">({a.text.length} char)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Componente riga campo ────────────────────────────────────────────────────

function FieldRow({
  fieldDef,
  original,
  correction,
  onChange,
  onReset,
  readOnly,
  markMissing,
  entityOriginal,
}: {
  fieldDef: FieldDef;
  original: string | number | null;
  correction?: string | number | null;
  onChange: (key: keyof BscData, value: string) => void;
  onReset?: (key: keyof BscData) => void;
  readOnly: boolean;
  /** Se true, un campo vuoto e' evidenziato con bordo rosso (campo core
   * mancante). Attivo solo su BL + Fattura; non su Cliente/CIF/supplementari
   * (opzionali per definizione). */
  markMissing?: boolean;
  /** Valore grezzo estratto dall'LLM prima della normalizzazione automatica
   * (apply_entity_corrections, BE) — presente solo se il campo e' stato
   * normalizzato in base a correzioni apprese da pratiche precedenti. */
  entityOriginal?: string | null;
}) {
  const hasCorrected = correction !== undefined && correction !== null && correction !== original;
  const displayValue = hasCorrected ? correction : original;
  const isEmpty = displayValue === null || displayValue === undefined || displayValue === "";
  const isNormalized = !!entityOriginal && entityOriginal !== String(original ?? "");
  const isMissing = !!markMissing && isEmpty;
  // Accento sul bordo dell'input: rosso per campo core mancante (solo cornice),
  // altrimenti giallo (classe .is-modified) per valore modificato. Il mancante
  // ha precedenza.
  const inputAccent = isMissing ? { borderColor: "var(--indaco-danger)" } : {};

  return (
    <tr id={fieldAnchorId(fieldDef.key)} style={{ borderBottom: "1px solid var(--indaco-gray-100)", scrollMarginTop: "80px", transition: "background-color 0.4s ease" }}>
      <td style={{ padding: "var(--space-2) var(--space-3)", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)", whiteSpace: "nowrap", width: "35%" }}>
        {fieldDef.label}
        {hasCorrected && !readOnly && (
          <span className="badge badge-warning" style={{ fontSize: "var(--font-size-xs)", padding: "1px 6px", marginLeft: "var(--space-2)", fontWeight: "var(--font-weight-semibold)" }} title="Valore modificato rispetto all'estrazione">
            modificato
          </span>
        )}
        {isNormalized && (
          <span className="badge badge-info" style={{ fontSize: "var(--font-size-xs)", padding: "1px 6px", marginLeft: "var(--space-2)", fontWeight: "var(--font-weight-semibold)" }} title="Nome normalizzato automaticamente in base a correzioni apprese da pratiche precedenti">
            normalizzato
          </span>
        )}
      </td>
      <td style={{ padding: "var(--space-2) var(--space-3)" }}>
        {readOnly ? (
          <span style={{ fontSize: "var(--font-size-sm)", color: isEmpty ? "var(--indaco-gray-300)" : "inherit", fontStyle: isEmpty ? "italic" : "normal" }}>
            {isEmpty ? "—" : String(displayValue)}
          </span>
        ) : (
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <input
              type={fieldDef.type === "number" ? "number" : "text"}
              className={`form-input${!isMissing && hasCorrected ? " is-modified" : ""}`}
              style={{ flex: 1, fontSize: "var(--font-size-sm)", ...inputAccent }}
              value={String(displayValue ?? "")}
              placeholder={isEmpty ? "(vuoto)" : undefined}
              onChange={(e) => onChange(fieldDef.key, e.target.value)}
            />
            {hasCorrected && onReset && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                title="Annulla modifica — ripristina il valore estratto"
                onClick={() => onReset(fieldDef.key)}
                style={{ padding: "var(--space-1)", minWidth: 32 }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>undo</span>
              </button>
            )}
          </div>
        )}
        {hasCorrected && (
          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", marginTop: "2px" }}>
            Originale: {original ?? "—"}
          </div>
        )}
        {!hasCorrected && isNormalized && (
          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-500)", marginTop: "2px" }}>
            Originale (LLM): {entityOriginal} → Normalizzato: {original}
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Sezione Condizioni di trasporto / CIF ───────────────────────────────────

/** True se l'Incoterm appartiene al gruppo C (CIF/CIP/CFR/CPT). */
function isCIncoterm(terms: string | null | undefined): boolean {
  if (!terms) return false;
  return C_INCOTERMS.includes(terms.trim().toUpperCase());
}

function toNumber(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Riga per i campi numerici CIF (Nolo / Assicurazione). */
function CifNumberRow({
  label,
  value,
  onChange,
  disabled,
  readOnly,
}: {
  label: string;
  value: string | number | null;
  onChange: (v: string) => void;
  disabled?: boolean;
  readOnly: boolean;
}) {
  const isDisabled = disabled ?? false;
  return (
    <div className="form-group" style={{ marginBottom: "var(--space-3)" }}>
      <label className="form-label">
        {label}
        {isDisabled && (
          <span className="text-muted" style={{ fontWeight: "normal", marginLeft: "var(--space-2)" }}>
            (N/A per questo Incoterm)
          </span>
        )}
      </label>
      {readOnly ? (
        <div className="text-sm" style={{ padding: "var(--space-2) 0", color: value === null || value === "" ? "var(--indaco-gray-300)" : "inherit", fontStyle: value === null || value === "" ? "italic" : "normal" }}>
          {value === null || value === "" ? "-" : String(value)}
        </div>
      ) : (
        <input
          type="number"
          step="0.01"
          className="form-input"
          placeholder="0.00"
          disabled={isDisabled}
          value={value === null || value === undefined ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function CifSection({
  original,
  corrections,
  invoiceTotal,
  onChange,
  readOnly,
}: {
  original: BscData | null;
  corrections: Partial<BscData>;
  invoiceTotal: number | null;
  onChange: (key: keyof BscData, value: string) => void;
  readOnly: boolean;
}) {
  // Valori effettivi = correzione se presente, altrimenti valore estratto.
  const terms = (corrections.terms_of_delivery ?? original?.terms_of_delivery ?? null) as string | null;
  const freight = corrections.freight ?? original?.freight ?? null;
  const insurance = corrections.insurance ?? original?.insurance ?? null;

  const incotermIsC = isCIncoterm(terms);
  const freightNum = toNumber(freight);
  const insuranceNum = toNumber(insurance);

  // FOB calcolato lato FE (solo per Incoterm C, con tutti i valori presenti).
  const fobValue = incotermIsC && invoiceTotal != null && freightNum != null && insuranceNum != null
    ? invoiceTotal - freightNum - insuranceNum
    : null;

  const filled = [
    terms, freight, insurance,
  ].filter(v => v !== null && v !== undefined && v !== "").length;

  return (
    <div className="card" style={{ marginBottom: "var(--space-4)", overflow: "hidden" }}>
      <div className="card-header-accent card-header-accent-info">
        <span className="material-symbols-outlined" style={{ fontSize: "20px", color: "var(--indaco-info)" }}>local_shipping</span>
        <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-info)" }}>
          Condizioni di trasporto / CIF
        </span>
        <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>
          {filled} / 3 campi
        </span>
      </div>

      <div style={{ padding: "var(--space-4)" }}>
        {/* Incoterm (select con free-text via datalist) */}
        <div className="form-group" style={{ marginBottom: "var(--space-3)" }}>
          <label className="form-label">Incoterm</label>
          {readOnly ? (
            <div className="text-sm" style={{ padding: "var(--space-2) 0", color: !terms ? "var(--indaco-gray-300)" : "inherit", fontStyle: !terms ? "italic" : "normal" }}>
              {terms ?? "-"}
            </div>
          ) : (
            <>
              <input
                type="text"
                list="bsc-incoterm-options"
                className="form-input"
                placeholder="es. CIF, FOB, EXW..."
                value={terms ?? ""}
                onChange={(e) => onChange("terms_of_delivery", e.target.value)}
              />
              <datalist id="bsc-incoterm-options">
                {INCOTERM_OPTIONS.map(o => <option key={o} value={o} />)}
              </datalist>
            </>
          )}
        </div>

        {/* Nolo e Assicurazione: editabili solo per Incoterm C */}
        <CifNumberRow
          label="Nolo (EUR)"
          value={freight}
          onChange={(v) => onChange("freight", v)}
          disabled={!incotermIsC}
          readOnly={readOnly}
        />
        <CifNumberRow
          label="Assicurazione (EUR)"
          value={insurance}
          onChange={(v) => onChange("insurance", v)}
          disabled={!incotermIsC}
          readOnly={readOnly}
        />

        {/* FOB calcolato (read-only, derivato) */}
        <div style={{
          marginTop: "var(--space-3)",
          padding: "var(--space-3) var(--space-4)",
          background: "var(--indaco-gray-50)",
          border: "1px dashed var(--indaco-gray-300)",
          borderRadius: "var(--border-radius-md)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)",
        }}>
          <div>
            <div className="form-label" style={{ marginBottom: 0 }}>Valore FOB (calcolato)</div>
            <div className="text-muted text-sm" style={{ marginTop: "2px" }}>
              FOB = Totale fattura - Nolo - Assicurazione
            </div>
          </div>
          <div style={{
            fontSize: "var(--font-size-lg)",
            fontWeight: "var(--font-weight-semibold)",
            color: fobValue != null ? "var(--indaco-info)" : "var(--indaco-gray-400)",
          }}>
            {fobValue != null
              ? fobValue.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : "-"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pannello campi mancanti (Phase 4) ────────────────────────────────────────

/** Id stabile dell'ancora di riga per un campo (usato da FieldRow e dal
 *  pannello "Campi mancanti" per il salto-al-campo). */
function fieldAnchorId(key: keyof BscData): string {
  return `bsc-field-${String(key)}`;
}

/** Porta il campo in vista, ne mette a fuoco l'input e lo evidenzia
 *  brevemente. Usa il DOM (getElementById) perche' il pannello e le righe
 *  campo vivono in sottoalberi separati: passare ref sarebbe piu' invasivo. */
function scrollToField(key: keyof BscData): void {
  const el = document.getElementById(fieldAnchorId(key));
  if (!el) return;
  // NB: niente behavior:"smooth" — su alcuni browser/reduced-motion e' un
  // no-op e il campo non viene raggiunto. Lo scroll istantaneo e' affidabile.
  el.scrollIntoView({ block: "center" });
  el.style.backgroundColor = "var(--indaco-warning-light)";
  window.setTimeout(() => { el.style.backgroundColor = ""; }, 1500);
  const input = el.querySelector("input, textarea, select") as HTMLElement | null;
  if (input) window.setTimeout(() => input.focus({ preventScroll: true }), 350);
}

/** Singolo pannello che elenca le LABEL dei campi nulli/vuoti attraverso
 *  BL + Invoice combinati (niente un pannello per sezione). I campi CIF NON
 *  sono mai inclusi qui: sono sempre opzionali/supplementari (coerente col
 *  denominatore di completeness, fisso a 26) e non vanno mai segnalati come
 *  "mancanti" indipendentemente dall'Incoterm.
 *  Riutilizza la stessa nozione di "valorizzato" di filledCount() qui sotto. */
function MissingFieldsPanel({
  bscData,
  corrections,
}: {
  bscData: BscData | null;
  corrections: Partial<BscData>;
}) {
  // Campi rilevanti per la completezza: sempre e solo i 26 core (BL + Invoice).
  const relevant: FieldDef[] = [...BL_FIELDS, ...INVOICE_FIELDS];

  const missing: FieldDef[] = relevant.filter((f) => {
    const v = corrections[f.key] ?? bscData?.[f.key];
    return v === null || v === undefined || v === "";
  });

  if (missing.length === 0) {
    return (
      <div className="card" style={{
        padding: "var(--space-3) var(--space-4)",
        display: "flex", alignItems: "center", gap: "var(--space-2)",
        borderLeft: "4px solid var(--indaco-success)",
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-success)" }}>check_circle</span>
        <span className="text-sm fw-semibold" style={{ color: "var(--indaco-success)" }}>
          Tutti i campi sono compilati
        </span>
      </div>
    );
  }

  return (
    <div className="card" style={{
      padding: "var(--space-2) var(--space-3)",
      borderLeft: "3px solid var(--indaco-warning)",
      display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap",
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-warning)" }}>priority_high</span>
      <span className="text-sm fw-semibold">Campi mancanti ({missing.length}):</span>
      {missing.map((f) => (
        <button
          key={String(f.key)}
          type="button"
          className="flow-chip"
          onClick={() => scrollToField(f.key)}
          title={`Vai al campo "${f.label}". I campi CIF (Incoterm/Nolo/Assicurazione) sono opzionali e non elencati qui.`}
        >
          {f.label}
          <span className="material-symbols-outlined icon-sm">arrow_forward</span>
        </button>
      ))}
    </div>
  );
}

// ── Paese di destinazione risolto (Phase 1) ─────────────────────────────────

/** Banner sintetico che mostra il paese di destinazione RISOLTO con priorita'
 * BL (pod_country) > Invoice (importer_country). Dati derivati dal BE, non
 * editabili. Indica la fonte (badge) ed evidenzia con warning le anomalie
 * (paese non africano — fuori dal mercato atteso di Frabemar). */
function DestinationCountryBanner({ practice }: { practice: BscPractice }) {
  const d = practice.bsc_data ?? null;
  // Il BE fornisce resolved_country/resolved_iso2/source/anomalia direttamente;
  // calcoliamo un fallback locale solo per sicurezza (record pre-Phase 1 senza
  // colonne popolate, es. dopo un backfill parziale).
  const pod = d?.pod_country ?? null;
  const imp = d?.importer_country ?? null;
  const resolved = practice.resolved_country ?? (pod ? pod : imp);
  const iso2 = practice.resolved_country_iso2 ?? toCountryIso2(resolved);
  const source = practice.resolved_country_source ?? (pod ? "bl" : imp ? "invoice" : null);
  const anomaly = practice.country_anomaly ?? false;

  if (!resolved) {
    return null; // nessun paese: niente banner (evita rumore visivo)
  }

  const sourceLabel = source === "bl"
    ? "dal Bill of Lading (POD)"
    : source === "invoice"
      ? "dalla fattura (importatore)"
      : "";

  return (
    <div className="card" style={{
      padding: "var(--space-3) var(--space-4)",
      display: "flex", alignItems: "center", gap: "var(--space-3)",
      borderColor: anomaly ? "var(--indaco-warning)" : "var(--border-color)",
      background: anomaly ? "var(--indaco-warning-light)" : undefined,
    }}>
      <span className="material-symbols-outlined" style={{
        fontSize: "22px",
        color: anomaly ? "var(--indaco-warning)" : "var(--indaco-brand-primary)",
      }}>
        {anomaly ? "warning" : "flag"}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="text-muted text-xs">Paese di destinazione (risolto)</div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-md)" }}>
            {iso2 ? `${iso2} — ${resolved}` : resolved}
          </span>
          {source && (
            <span className={`badge ${source === "bl" ? "badge-info" : "badge-neutral"} badge-sm`}>
              {sourceLabel}
            </span>
          )}
        </div>
        {anomaly && (
          <div className="text-xs" style={{ color: "var(--indaco-warning)", marginTop: 2 }}>
            Paese non africano — fuori dal mercato atteso. Verificare la destinazione.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sezione supplementare (Packing List / EX1) — Phase 5 ─────────────────────

/** Sezione riutilizzabile per i campi supplementari Packing List / EX1.
 *
 * Renderizzata solo quando almeno uno dei campi del gruppo e' non-null
 * (il documento corrispondente e' presente). I campi supplementari NON
 * conteggiano in completeness — sono additivi e inclusi nel JSON di output.
 */
function SupplementSection({
  title,
  icon,
  color,
  fields,
  original,
  corrections,
  onChange,
  onReset,
  readOnly,
}: {
  title: string;
  icon: string;
  color: "info" | "warning";
  fields: FieldDef[];
  original: BscData | null;
  corrections: Partial<BscData>;
  onChange: (key: keyof BscData, value: string) => void;
  onReset?: (key: keyof BscData) => void;
  readOnly: boolean;
}) {
  // Nascondi la sezione se nessun campo del gruppo e' presente (nessun doc).
  const hasAny = fields.some(
    f => (corrections[f.key] ?? original?.[f.key]) != null
      && (corrections[f.key] ?? original?.[f.key]) !== ""
  );
  if (!hasAny) return null;

  const filled = fields.filter(
    f => (corrections[f.key] ?? original?.[f.key]) != null
      && (corrections[f.key] ?? original?.[f.key]) !== ""
  ).length;

  const colorVar = color === "info" ? "var(--indaco-info)" : "var(--indaco-warning)";

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className={`card-header-accent card-header-accent-${color}`}>
        <span className="material-symbols-outlined" style={{ fontSize: "20px", color: colorVar }}>{icon}</span>
        <span style={{ fontWeight: "var(--font-weight-semibold)", color: colorVar }}>{title}</span>
        <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>
          {filled} / {fields.length} campi
        </span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {fields.map(field => (
            <FieldRow
              key={field.key}
              fieldDef={field}
              original={original?.[field.key] ?? null}
              correction={corrections[field.key]}
              onChange={onChange}
              onReset={onReset}
              readOnly={readOnly}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Pannello Attività (audit) ────────────────────────────────────────────────
// Cronologia "chi ha fatto cosa" sulla pratica: stessa fonte (audit_log) della
// pagina globale "Registro attività" di Casadei, filtrata su bsc_practice_id.

const BSC_ACTION_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  approve:         { label: "Approvata",          color: "var(--indaco-success)", icon: "check_circle" },
  reject:          { label: "Rifiutata",          color: "var(--indaco-danger)",  icon: "cancel" },
  discard:         { label: "Scartata",           color: "var(--indaco-gray-500)", icon: "delete_sweep" },
  reprocess:       { label: "Riprocessata",       color: "var(--indaco-brand-primary)", icon: "restart_alt" },
  reopen:          { label: "Riaperta",           color: "var(--indaco-warning)", icon: "lock_open" },
  document_upload: { label: "Documento caricato", color: "var(--indaco-info)",    icon: "upload_file" },
  document_delete: { label: "Documento eliminato", color: "var(--indaco-warning)", icon: "delete" },
  bsc_correction:  { label: "Correzione",         color: "var(--indaco-info)",    icon: "edit" },
};

function _fmtActivityDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function ActivityPanel({ practiceId, version }: { practiceId: string; version?: string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchBscActivity(practiceId)
      .then((r) => { if (alive) setEntries(r); })
      .catch(() => { if (alive) setEntries([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [practiceId, version]);

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="card-header-accent card-header-accent-neutral">
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-gray-600)" }}>history</span>
        <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-700)" }}>Attività</span>
        <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>chi ha fatto cosa</span>
      </div>
      {loading ? (
        <div style={{ padding: "var(--space-4)", textAlign: "center" }}><span className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div className="text-muted text-sm" style={{ padding: "var(--space-4)" }}>Nessuna attività registrata su questa pratica.</div>
      ) : (
        <div style={{ padding: "var(--space-2) 0" }}>
          {entries.map((e) => {
            const meta = BSC_ACTION_LABELS[e.action] ?? { label: e.action, color: "var(--indaco-gray-500)", icon: "bolt" };
            const isOpen = openId === e.id;
            const hasDetail = !!(e.details || e.feedback_note || e.lesson_learned);
            return (
              <div key={e.id} style={{ padding: "var(--space-2) var(--space-4)", borderBottom: "1px solid var(--indaco-gray-100)" }}>
                <div
                  role={hasDetail ? "button" : undefined}
                  tabIndex={hasDetail ? 0 : undefined}
                  style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: hasDetail ? "pointer" : "default" }}
                  onClick={() => hasDetail && setOpenId(isOpen ? null : e.id)}
                  onKeyDown={(e2) => { if (hasDetail && (e2.key === "Enter" || e2.key === " ")) { e2.preventDefault(); setOpenId(isOpen ? null : e.id); } }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: meta.color }}>{meta.icon}</span>
                  <span style={{ fontWeight: "var(--font-weight-medium)", color: meta.color }}>{meta.label}</span>
                  <span className="text-muted text-sm">·&nbsp;{e.operator_name || e.operator_id || "—"}</span>
                  <span className="text-muted text-sm" style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>{_fmtActivityDate(e.created_at)}</span>
                  {hasDetail && (
                    <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-gray-400)" }}>
                      {isOpen ? "expand_less" : "expand_more"}
                    </span>
                  )}
                </div>
                {isOpen && hasDetail && (
                  <div style={{ marginTop: "var(--space-2)", paddingLeft: 26, fontSize: "var(--font-size-sm)" }}>
                    {e.feedback_note && (
                      <div><span className="text-muted fw-medium">Nota: </span>{e.feedback_note}</div>
                    )}
                    {e.lesson_learned && (
                      <div style={{ color: "var(--indaco-success)" }}><span className="text-muted fw-medium">Apprendimento: </span>{e.lesson_learned}</div>
                    )}
                    {e.details && (
                      <pre style={{ fontSize: "var(--font-size-xs)", whiteSpace: "pre-wrap", margin: "var(--space-1) 0 0", color: "var(--indaco-gray-700)" }}>
                        {JSON.stringify(e.details, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Componente principale ────────────────────────────────────────────────────

export function BscDetail() {
  const { practiceId } = useParams<{ practiceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // La lista (BscList.tsx) passa la propria querystring (filtro/pagina/ricerca
  // attivi) in location.state quando naviga qui. Senza questo, "Indietro"
  // faceva sempre navigate("/bsc") senza parametri, che ripiomba sempre sul
  // filtro di default (pending_review/"Da rivedere") perdendo qualsiasi altro
  // filtro attivo (Doc. mancanti, Scartate, ricerca...).
  const backToListPath = `/bsc${(location.state as { fromQuery?: string } | null)?.fromQuery ?? ""}`;
  const [practice, setPractice] = useState<BscPractice | null>(null);
  const [corrections, setCorrections] = useState<Partial<BscData>>({});
  const [approveNote, setApproveNote] = useState("");
  const [noteAction, setNoteAction] = useState<null | "reject" | "discard">(null);
  // Conferma esplicita per approvare una pratica INCOMPLETA (manca BL/fattura o
  // sotto soglia 88%): genererebbe un certificato parziale → si chiede l'ok.
  const [approveConfirm, setApproveConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!practiceId) return;
    setIsLoading(true);
    try {
      const p = await getBscPractice(practiceId);
      setPractice(p);
      setCorrections(p.corrections ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore caricamento");
    } finally {
      setIsLoading(false);
    }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  // Segna la pratica come letta all'apertura del dettaglio (stato CONDIVISO di
  // team). Fire-and-forget: non blocca il caricamento, l'endpoint e' idempotente
  // e monotono (solo la prima apertura da chiunque scrive letta_da/letta_il).
  useEffect(() => {
    if (!practiceId) return;
    markBscPracticeRead(practiceId).catch(() => {});
  }, [practiceId]);

  const handleFieldChange = (key: keyof BscData, value: string) => {
    setCorrections(prev => ({ ...prev, [key]: value === "" ? null : value }));
  };

  /** Annulla la singola modifica — ripristina il valore estratto. */
  const handleFieldReset = (key: keyof BscData) => {
    setCorrections(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /** Azzera tutte le corrections. */
  const resetAllCorrections = () => {
    setCorrections({});
  };

  const handleApprove = async () => {
    if (!practiceId || !practice) return;
    // Guardia incompletezza (Fase D, 2026-07-23): manca il minimo HANJA o
    // non tutti i campi obbligatori-per-completo → il certificato sarebbe
    // parziale. Prima approvazione mostra il warning, la seconda
    // (approveConfirm) procede comunque (override consapevole).
    const h = practice.llm_metadata?.hanja;
    const incomplete = !h?.minimal_ready || (h?.complete_non_null ?? 0) < (h?.complete_total ?? 1);
    if (incomplete && !approveConfirm) {
      setApproveConfirm(true);
      return;
    }
    setIsSaving(true);
    try {
      const res = await approveBscPractice(practiceId, corrections, approveNote);
      showToast(`Pratica approvata. Output: ${res.output_path}`);
      setApproveConfirm(false);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore approvazione", false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async (note: string) => {
    if (!practiceId || !note.trim()) return;
    setIsSaving(true);
    try {
      await rejectBscPractice(practiceId, [], note);
      showToast("Pratica rifiutata");
      setNoteAction(null);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore rifiuto", false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = async (note: string) => {
    if (!practiceId || !note.trim()) return;
    setIsSaving(true);
    try {
      const res = await discardBscPractice(practiceId, note);
      const blobs = res.output_cleanup?.blobs_deleted ?? 0;
      showToast(`Pratica scartata definitivamente${blobs ? ` (${blobs} file output rimossi)` : ""}`);
      setNoteAction(null);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore scarto", false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReprocess = async () => {
    if (!practiceId) return;
    setIsSaving(true);
    try {
      const res = await reprocessBscPractice(practiceId);
      showToast(`Reprocessing completato: ${Math.round(res.completeness * 100)}% completezza`);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore reprocessing", false);
    } finally {
      setIsSaving(false);
    }
  };

  // Anteprima JSON di output (dry-run): mostra esattamente cosa verrebbe generato
  // all'approvazione, con le correzioni correnti. Fetch on-open (riflette gli edit).
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [jsonPreview, setJsonPreview] = useState<Record<string, unknown> | null>(null);
  const [jsonLoading, setJsonLoading] = useState(false);

  const toggleJsonPreview = async () => {
    if (showJsonPreview) { setShowJsonPreview(false); return; }
    setShowJsonPreview(true);
    setJsonLoading(true);
    try {
      const p = await bscOutputPreview(practiceId!, corrections as Record<string, unknown>);
      setJsonPreview(p);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Errore anteprima JSON", false);
      setJsonPreview(null);
    } finally {
      setJsonLoading(false);
    }
  };

  const downloadJsonPreview = () => {
    if (!jsonPreview) return;
    const blob = new Blob([JSON.stringify(jsonPreview, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const bl = (practice?.bsc_data?.bl_number as string | undefined) || practice?.id?.slice(0, 8) || "output";
    a.download = `bsc_${bl}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <div className="loading-center"><div className="spinner-lg" /></div>;
  if (error || !practice) return (
    <div style={{ padding: "var(--space-6)" }}>
      <div className="card" style={{ borderLeft: "4px solid var(--indaco-danger)", padding: "var(--space-4)" }}>
        <span style={{ color: "var(--indaco-danger)" }}>{error ?? "Pratica non trovata"}</span>
      </div>
    </div>
  );

  const isReadOnly = practice.status === "approved" || practice.status === "rejected" || practice.status === "discarded";
  const bscData = practice.bsc_data;
  // Le due soglie HANJA (Fase D, 2026-07-23) sono ora l'indicatore primario
  // — sostituiscono la vecchia metrica interna a 26 campi core.
  const hanja = practice.llm_metadata?.hanja;

  // Valori effettivi = correzione (se presente) altrimenti valore estratto.
  const eff = (k: keyof BscData) => corrections[k] ?? bscData?.[k];
  const effectiveInvoiceTotal = typeof eff("invoice_total") === "string"
    ? Number(eff("invoice_total"))
    : (eff("invoice_total") as number | null);

  // Conta solo le corrections effettivamente diverse dall'estratto.
  const effectiveCorrectionsCount = (() => {
    if (!bscData) return 0;
    let n = 0;
    const norm = (x: unknown) => (x === undefined || x === null ? "" : String(x));
    for (const [k, v] of Object.entries(corrections)) {
      const orig = (bscData as unknown as Record<string, unknown>)[k];
      if (norm(v) !== norm(orig)) n += 1;
    }
    return n;
  })();

  function filledCount(fields: FieldDef[]) {
    return fields.filter(f => {
      const v = corrections[f.key] ?? bscData?.[f.key];
      return v !== null && v !== undefined && v !== "";
    }).length;
  }

  return (
    <>
      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={toast.ok ? "toast toast-success" : "toast toast-error"}>
            {toast.msg}
          </div>
        </div>
      )}

      <DetailPageShell
        backTo={backToListPath}
        backLabel="Indietro"
        title="Pratica BSC"
        titleId={practice.id}
        badges={<StatusBadge status={practice.status} />}
        meta={
          // Certificate ID in TESTA, accanto a titolo e stato: richiesta di
          // Massimo in call il 2026-07-29 — è il riferimento univoco della
          // pratica verso HANJA, quindi sta in intestazione e non in un
          // pannello laterale.
          <HanjaCertificateId value={practice.hanja_certificate_id} compact />
        }
        approve={!isReadOnly ? { onClick: handleApprove, disabled: isSaving, label: approveConfirm ? "Approva comunque" : "Approva" } : undefined}
        reprocess={!isReadOnly ? { onClick: handleReprocess, disabled: isSaving, label: "Riprocessa con LLM" } : undefined}
        reject={!isReadOnly ? { onClick: () => setNoteAction("reject"), disabled: isSaving } : undefined}
        extraActions={
          <>
            {!isReadOnly && (
              <button className="btn btn-outline btn-sm" onClick={() => setNoteAction("discard")} disabled={isSaving}
                title="Chiude definitivamente la pratica (fuori scope, duplicato). NON riprocessabile.">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete_sweep</span> Scarta
              </button>
            )}
            {!isReadOnly && approveConfirm && (
              <button className="btn btn-outline btn-sm" onClick={() => setApproveConfirm(false)} disabled={isSaving}>Annulla</button>
            )}
            <button className="btn btn-outline btn-sm" onClick={toggleJsonPreview}
              title="Mostra il JSON che verrà generato all'approvazione">
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>data_object</span> JSON
            </button>
          </>
        }
        readOnlyMessage={isReadOnly ? "Sola lettura" : undefined}
      >
        {!isReadOnly && approveConfirm && (
          <div className="alert alert-warning" style={{ fontSize: "var(--font-size-xs)" }}>
            <span className="material-symbols-outlined icon-sm">warning</span>
            <span>Pratica incompleta: il certificato sarà parziale. Premi "Approva comunque" per procedere.</span>
          </div>
        )}

      {/* Anteprima JSON di output (Task 15/07): mostra/scarica esattamente il
          JSON che verrebbe generato all'approvazione, con le correzioni correnti. */}
      {showJsonPreview && (
        <div className="card" style={{ marginBottom: "var(--space-3)", overflow: "hidden" }}>
          <div className="card-header-accent card-header-accent-neutral" style={{ flexWrap: "wrap" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>data_object</span>
            <span style={{ fontWeight: "var(--font-weight-semibold)" }}>Anteprima JSON di output</span>
            <span className="text-muted text-xs">come verrà generato all'approvazione (correzioni correnti applicate)</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: "var(--space-2)" }}>
              <button className="btn btn-primary btn-sm" onClick={downloadJsonPreview} disabled={!jsonPreview}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span className="material-symbols-outlined icon-sm">download</span> Scarica JSON
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowJsonPreview(false)}>Chiudi</button>
            </div>
          </div>
          {jsonLoading ? (
            <div style={{ padding: "var(--space-4)", textAlign: "center" }}><span className="spinner" /></div>
          ) : (
            <pre style={{ margin: 0, padding: "var(--space-3)", maxHeight: 420, overflow: "auto", fontSize: "var(--font-size-xs)", fontFamily: "var(--font-family-mono)", background: "var(--indaco-gray-50)", color: "var(--indaco-gray-800)" }}>
              {jsonPreview ? JSON.stringify(jsonPreview, null, 2) : "—"}
            </pre>
          )}
        </div>
      )}

      {/* Errore pipeline */}
      {practice.error_message && (
        <div className="card" style={{ borderLeft: "4px solid var(--indaco-danger)", padding: "var(--space-3)", marginBottom: "var(--space-3)", color: "var(--indaco-danger)", fontSize: "var(--font-size-sm)" }}>
          Errore pipeline: {practice.error_message}
        </div>
      )}

      {/* Campi mancanti IN TESTA (Task 3): l'operatore vede subito cosa manca
          (es. P.IVA importatore) senza doverlo cercare nel documento. Prominente
          soprattutto per le pratiche in "Doc. mancanti". */}
      <div style={{ marginBottom: "var(--space-3)" }}>
        <MissingFieldsPanel bscData={bscData} corrections={corrections} />
      </div>

      {/* Body scrollabile (scroll interno) */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {/* Layout 2 colonne (stile Rinascente) */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(380px, 1fr) minmax(420px, 1.3fr)", gap: "var(--space-4)", alignItems: "start", minWidth: 0 }}>

        {/* ── Colonna sinistra: documenti originali + email ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", minWidth: 0 }}>
          {/* Metadati (compact) */}
          <div className="card" style={{ padding: "var(--space-3)", display: "flex", gap: "var(--space-4)", flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div className="text-muted text-xs">Documenti</div>
              <div style={{ display: "flex", gap: "var(--space-1)", marginTop: "var(--space-1)" }}>
                <span className={`badge ${practice.has_bl ? "badge-info" : "badge-neutral"} badge-sm`}>BL</span>
                <span className={`badge ${practice.has_invoice ? "badge-info" : "badge-neutral"} badge-sm`}>INV</span>
              </div>
            </div>
            <div>
              <div className="text-muted text-xs">Ricevuta</div>
              <div className="text-sm">{fmt(practice.created_at)}</div>
            </div>
            {practice.approved_at && (
              <div>
                <div className="text-muted text-xs">Approvata</div>
                <div className="text-sm">{fmt(practice.approved_at)}</div>
              </div>
            )}
          </div>

          {/* Soglie HANJA (Fase D, 2026-07-23) — indicatore primario, sostituisce
              la vecchia metrica interna a 26 campi core. */}
          <div className="card" style={{ padding: "var(--space-3)" }}>
            <div className="text-muted text-xs" style={{ marginBottom: "var(--space-2)" }}>Certificato HANJA</div>
            <HanjaReadinessPanel hanja={hanja} />
          </div>

          <DocumentsPanel practice={practice} onProcessed={load} showToast={showToast} isReadOnly={isReadOnly} />
          {/* Upload ammesso anche su pratica APPROVATA (la riapre) — solo
              'discarded' è chiusa definitivamente. Allineato al backend
              (_check_practice_editable con allow_approved_reopen=True). */}
          {practice.status !== "discarded" && (
            <DocumentUploadPanel practice={practice} onProcessed={load} showToast={showToast} />
          )}
          <EmailsPanel emails={practice.raw_emails ?? []} />
        </div>

        {/* ── Colonna destra: dati estratti + editing + azioni ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {/* (Campi mancanti spostati IN TESTA alla pratica — Task 3) */}
          {/* Paese di destinazione risolto (Phase 1) — priorita' BL pod_country
              > importer_country. Il BE calcola gia' il valore risolto + l'anomalia;
              qui mostriamo un banner sintetico con fonte + eventuale warning
              (paese non africano). Non e' editabile (deriva dai campi BL/Invoice). */}
          <DestinationCountryBanner practice={practice} />
          {/* Sezione Cliente (da email) — chi scrive materialmente a Frabemar,
              dalla firma. Metadato non contrattuale, editabile. */}
          <div className="card" style={{ overflow: "hidden" }}>
            <div className="card-header-accent card-header-accent-neutral">
              <span className="material-symbols-outlined" style={{ fontSize: "20px", color: "var(--indaco-gray-600)" }}>alternate_email</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-700)" }}>Cliente (da email)</span>
              <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ClientSourceChip source={bscData?.client_email_source} />
                <span className="text-muted text-sm">non nel JSON di output</span>
              </span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {CLIENT_FIELDS.map(field => (
                  <FieldRow
                    key={field.key}
                    fieldDef={field}
                    original={bscData?.[field.key] ?? null}
                    correction={corrections[field.key]}
                    onChange={handleFieldChange}
                    onReset={handleFieldReset}
                    readOnly={isReadOnly}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Sezione Bill of Lading */}
          <div className="card" style={{ overflow: "hidden" }}>
            <div className="card-header-accent card-header-accent-brand">
              <span className="material-symbols-outlined" style={{ fontSize: "20px", color: "var(--indaco-brand-primary)" }}>directions_boat</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-brand-primary)" }}>Bill of Lading</span>
              <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>
                {filledCount(BL_FIELDS)} / {BL_FIELDS.length} campi
              </span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {BL_FIELDS.map(field => (
                  <FieldRow
                    key={field.key}
                    fieldDef={field}
                    original={bscData?.[field.key] ?? null}
                    correction={corrections[field.key]}
                    onChange={handleFieldChange}
                    onReset={handleFieldReset}
                    readOnly={isReadOnly}
                    markMissing
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Sezione Fattura Commerciale */}
          <div className="card" style={{ overflow: "hidden" }}>
            <div className="card-header-accent card-header-accent-success">
              <span className="material-symbols-outlined" style={{ fontSize: "20px", color: "var(--indaco-success)" }}>receipt_long</span>
              <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-success)" }}>Fattura Commerciale</span>
              <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>
                {filledCount(INVOICE_FIELDS)} / {INVOICE_FIELDS.length} campi
              </span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {INVOICE_FIELDS.map(field => (
                  <FieldRow
                    key={field.key}
                    fieldDef={field}
                    original={bscData?.[field.key] ?? null}
                    correction={corrections[field.key]}
                    onChange={handleFieldChange}
                    onReset={handleFieldReset}
                    readOnly={isReadOnly}
                    markMissing
                    entityOriginal={practice.llm_metadata?.entity_corrections?.[field.key] ?? null}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Sezione Condizioni di trasporto / CIF */}
          <CifSection
            original={bscData}
            corrections={corrections}
            invoiceTotal={effectiveInvoiceTotal}
            onChange={handleFieldChange}
            readOnly={isReadOnly}
          />

          {/* Sezione Packing List (Phase 5) — solo se il documento e' presente */}
          <SupplementSection
            title="Packing List"
            icon="inventory_2"
            color="info"
            fields={PACKING_LIST_FIELDS}
            original={bscData}
            corrections={corrections}
            onChange={handleFieldChange}
            onReset={handleFieldReset}
            readOnly={isReadOnly}
          />

          {/* Sezione Dichiarazione doganale EX1 (Phase 5) — solo se presente */}
          <SupplementSection
            title="Dichiarazione doganale EX1"
            icon="verified_user"
            color="warning"
            fields={EX1_FIELDS}
            original={bscData}
            corrections={corrections}
            onChange={handleFieldChange}
            onReset={handleFieldReset}
            readOnly={isReadOnly}
          />

          {/* Attività: cronologia azioni operatore (audit) — si aggiorna quando
              la pratica cambia (updated_at) dopo approva/rifiuta/riprocessa/ecc. */}
          <ActivityPanel practiceId={practice.id} version={practice.updated_at} />

          {/* Azioni (solo se non è già approvata/rifiutata) */}
          {!isReadOnly && (
            <div className="card" style={{ padding: "var(--space-4)" }}>
              <h3 style={{ margin: "0 0 var(--space-3)", fontSize: "var(--font-size-base)", fontWeight: "var(--font-weight-semibold)" }}>
                Azioni
              </h3>

              {effectiveCorrectionsCount > 0 && (
                <div className="badge badge-warning" style={{ marginBottom: "var(--space-3)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-2)" }}>
                  <span>{effectiveCorrectionsCount} correzion{effectiveCorrectionsCount === 1 ? "e" : "i"} da applicare</span>
                  <button className="btn btn-ghost btn-sm" onClick={resetAllCorrections} disabled={isSaving}
                    title="Annulla tutte le modifiche e ripristina i valori estratti"
                    style={{ padding: "2px 8px", fontSize: "var(--font-size-xs)" }}>
                    <span className="material-symbols-outlined icon-sm">restart_alt</span>
                    Azzera tutto
                  </button>
                </div>
              )}

              {/* Note approvazione */}
              <div className="form-group" style={{ marginBottom: "var(--space-3)" }}>
                <label className="form-label">Nota approvazione (opzionale)</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  placeholder="Eventuali note per l'approvazione..."
                  disabled={isSaving}
                />
              </div>

              {/* Rifiuto/Scarto ora via NoteModal (fase 3); le azioni sono nella
                  ReviewActionBar sticky in alto. Qui resta solo la nota di
                  approvazione (opzionale). */}
            </div>
          )}

          {/* Modello LLM (dettagli tecnici collassabili) */}
          {practice.llm_metadata && (
            <details>
              <summary style={{ cursor: "pointer", fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-500)", padding: "var(--space-2)" }}>
                Dettagli analisi LLM
              </summary>
              <div className="card" style={{ marginTop: "var(--space-2)", padding: "var(--space-3)", fontSize: "var(--font-size-sm)" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "var(--space-3)" }}>
                  <div><span className="text-muted">Modello</span><br />{practice.llm_metadata.model}</div>
                  <div><span className="text-muted">Token input</span><br />{practice.llm_metadata.prompt_tokens ?? "—"}</div>
                  <div><span className="text-muted">Token output</span><br />{practice.llm_metadata.completion_tokens ?? "—"}</div>
                  <div><span className="text-muted">Latenza</span><br />{Math.round(practice.llm_metadata.latency_ms)} ms</div>
                </div>
              </div>
            </details>
          )}
        </div>
      </div>
      </div>

      {/* Rifiuto / Scarto via NoteModal (fase 3) */}
      {noteAction === "reject" && (
        <NoteModal
          title="Rifiuta pratica"
          label="Motivo del rifiuto (obbligatorio)"
          placeholder="Specifica il motivo del rifiuto..."
          confirmLabel="Rifiuta"
          confirmVariant="danger"
          loading={isSaving}
          onConfirm={handleReject}
          onClose={() => setNoteAction(null)}
        />
      )}
      {noteAction === "discard" && (
        <NoteModal
          title="Scartare definitivamente la pratica?"
          label="Motivo dello scarto (obbligatorio)"
          placeholder="Specifica il motivo dello scarto..."
          confirmLabel="Sì, scarta definitivamente"
          confirmVariant="danger"
          loading={isSaving}
          warning={
            <div className="alert alert-warning" style={{ marginBottom: "var(--space-2)", flexDirection: "column", alignItems: "stretch", fontSize: "var(--font-size-sm)" }}>
              <p style={{ margin: "0 0 var(--space-2)" }}>La pratica verrà chiusa definitivamente (stato <strong>Scartata</strong>).</p>
              <ul style={{ margin: 0, paddingLeft: "var(--space-4)" }}>
                <li>NON sarà più riprocessabile automaticamente.</li>
                <li>Per riaprirla servirà un intervento manuale (stato → Da rivedere).</li>
                <li>Resta nello storico per audit ma esce dal flusso attivo.</li>
                {practice.status === "approved" && (<li>Il JSON di output su filesystem verrà cancellato (best-effort).</li>)}
              </ul>
            </div>
          }
          onConfirm={handleDiscard}
          onClose={() => setNoteAction(null)}
        />
      )}
    </DetailPageShell>
    </>
  );
}
